import React, { FC, useState } from 'react'
import { Col, DatePicker, Input, Modal, notification, Row, Steps, Tag } from 'antd'
import RNavigator from '../../components/RNavigator'
import PlantForm from './PlantForm'
import StorageForm from './StorageForm'
import SceneriosForm from './ScenariosForm'
import type { RangePickerProps } from 'antd/es/date-picker'
import { getMomentDateDiff, formatMomentToApiDate } from '../../utils'
import { IPlantForm } from '../../types/plant'
import { IStorageSystemForm } from '../../types/storageSystem'
import { IScenarioInitialValues } from '../../types/scenario'
import { simulationApi } from '../../api'
import { useHistory } from 'react-router-dom'
import PATHS from '../../routes/paths'

const { Step } = Steps

const STEPS = {
  PLANT: { title: 'Plant', value: 'plant' },
  STORAGE: { title: 'Storage', value: 'storage' },
  SCENARIOS: { title: 'Scenarios', value: 'scenarios' },
}
const steps = Object.values(STEPS)

const New: FC = () => {
  const history = useHistory()

  const [currentStepIdx, setCurrentStepIdx] = useState(0)
  const [stepCompletionPercent] = useState(0)
  const [range, setRange] = useState<RangePickerProps['value']>()

  const [plants, setPlants] = useState<IPlantForm[]>([])
  const [storageSystems, setStorageSystems] = useState<IStorageSystemForm>()
  const [scenarios, setScenarios] = useState<IScenarioInitialValues>()

  const [scenarioName, setScenarioName] = useState('')
  const [isLoadingSubmit, setIsLoadingSubmit] = useState(false)
  const [isSubmitModalVisible, setIsSubmitModalVisible] = useState(false)

  function getIsCurrentStep(stepValue: string) {
    const currentStep = steps[currentStepIdx]
    return stepValue === currentStep.value
  }

  function goNext() {
    return setCurrentStepIdx((curIdx) => curIdx + 1)
  }
  function goBack() {
    return setCurrentStepIdx((curIdx) => curIdx - 1)
  }

  async function startOptimization() {
    if (!storageSystems || !range?.[0] || !range?.[1] || !scenarios) {
      notification.error({ message: 'Gerekli alanları doldurup tekrar deneyiniz' })
      return
    }
    try {
      setIsLoadingSubmit(true)
      const startDate = formatMomentToApiDate(range[0])
      const endDate = formatMomentToApiDate(range[1])

      const plantsPostData = plants.map((plant) => ({
        name: '',
        flag: false,
        generationType: plant.generationType,
        installedPowerKw: plant.installedPowerKw,
        licensedPowerKw: plant.licensedPowerKw,
        feedInTariffDollars: plant.feedInTariffDollars,
        url: plant.generationFile?.[0]?.url || '',
        feedInTariffDollarsEndDate: plant.feedInTariffDollarsEndDate?.toISOString() || null,
      }))
      const storageSystemPostData = { ...storageSystems, name: '' }
      const { sellToGrid, purchaseFromFrom, storageSellPriceLimitDollars, timeSpan } = scenarios
      const loadForecastId = scenarios?.loadForecast?.[0].url || ''
      const priceForecastId = scenarios?.dayAheadPrices?.[0].url || ''
      const primaryFrequencyControlPriceId = scenarios?.primaryFrequencyPrices?.[0].url || ''

      notification.success({ message: 'Optimizasyon başlatıldı' })

      await simulationApi.optimize({
        startDate,
        endDate,
        scenarioName: scenarioName,
        plants: plantsPostData,
        storage: storageSystemPostData,
        priceForecastId,
        loadForecastId,
        primaryFrequencyControlPriceId,
        sellToGrid: sellToGrid ? 1 : 0,
        purchaseFromGrid: purchaseFromFrom ? 1 : 0,
        storageCellPrice: storageSellPriceLimitDollars,
        timeDelta: timeSpan,
      })

      notification.success({ message: 'Optimizasyon başarıyla sonuçlandı' })
      history.push(PATHS.results)
    } catch {
      notification.success({ message: 'Optimizasyon sonuçlanamadı' })
      setIsSubmitModalVisible(false)
    } finally {
      setIsLoadingSubmit(false)
    }
  }

  console.log(scenarios)

  return (
    <>
      <Row className='mb-12' align='middle' gutter={8}>
        <Col>Date Range: </Col>
        <Col>
          <DatePicker.RangePicker value={range} onChange={(dates) => setRange(dates)} />
        </Col>
        {range?.[0] && range?.[1] && (
          <Col>
            <Tag className='py-4' color='blue'>
              <b>{getMomentDateDiff(range[0], range[1])}</b> Day(s)
            </Tag>
          </Col>
        )}
      </Row>

      <Steps
        className='mb-48'
        current={currentStepIdx}
        onChange={setCurrentStepIdx}
        percent={stepCompletionPercent}
      >
        {steps.map(({ title, value }) => (
          <Step title={title} key={value} />
        ))}
      </Steps>
      <Row gutter={40}>
        <Col span={12}>
          <section className={getIsCurrentStep(STEPS.PLANT.value) ? '' : 'd-none'}>
            <PlantForm
              allowMultiple
              displayOverlayTriggerButton
              onValuesChange={(_, values) => setPlants(values.plants || [])}
            />
          </section>
          <section className={getIsCurrentStep(STEPS.STORAGE.value) ? '' : 'd-none'}>
            <StorageForm
              displayOverlayTriggerButton
              onValuesChange={(_, values) => setStorageSystems(values)}
            />
          </section>
          <section className={getIsCurrentStep(STEPS.SCENARIOS.value) ? '' : 'd-none'}>
            <SceneriosForm
              displayOverlayTriggerButton
              onValuesChange={(_, values) => setScenarios(values)}
            />
          </section>
        </Col>
        <Col span={12}>{/* <FormSummary /> */}</Col>
      </Row>
      <RNavigator
        displayBack={currentStepIdx !== 0}
        displayNext={currentStepIdx !== steps.length - 1}
        displaySubmit={currentStepIdx === steps.length - 1}
        onNext={goNext}
        onBack={goBack}
        onSubmit={() => setIsSubmitModalVisible(true)}
      />

      <Modal
        title='Scenario Name'
        okText='Submit'
        visible={isSubmitModalVisible}
        onCancel={() => setIsSubmitModalVisible(false)}
        onOk={startOptimization}
        okButtonProps={{
          disabled: !scenarioName,
          loading: isLoadingSubmit,
        }}
      >
        <Input onChange={(e) => setScenarioName(e.target.value)} />
      </Modal>
    </>
  )
}

export default New
